
// Local interface matching the expected GenAI Blob structure
export interface GenAIBlob {
  data: string;
  mimeType: string;
}

/**
 * Decodes a base64 string to a Uint8Array.
 * Manual implementation as required by GenAI guidelines.
 */
export function decode(base64: string): Uint8Array {
  const binaryString = atob(base64);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}

/**
 * Encodes a Uint8Array to a base64 string.
 * Manual implementation as required by GenAI guidelines.
 */
export function encode(bytes: Uint8Array): string {
  let binary = '';
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/**
 * Encodes Float32 audio data (from Web Audio API) to a base64 string 
 * representing PCM 16-bit integer data.
 */
export function base64EncodeAudio(float32Data: Float32Array): string {
  const len = float32Data.length;
  const int16 = new Int16Array(len);
  for (let i = 0; i < len; i++) {
    // Clamp values to [-1, 1] and scale to 16-bit integer range
    const s = Math.max(-1, Math.min(1, float32Data[i]));
    int16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
  }
  
  return encode(new Uint8Array(int16.buffer));
}

/**
 * Creates a Blob object for the Live API from raw Float32 audio data.
 */
export function createAudioBlob(data: Float32Array, sampleRate: number): GenAIBlob {
  return {
    data: base64EncodeAudio(data),
    mimeType: `audio/pcm;rate=${sampleRate}`,
  };
}

/**
 * Decodes a Uint8Array containing PCM 16-bit data into an AudioBuffer.
 * Signature updated to take Uint8Array as per GenAI guidelines and usage in App.tsx.
 */
export async function decodeAudioData(
  data: Uint8Array,
  ctx: AudioContext,
  sampleRate: number = 24000,
  numChannels: number = 1
): Promise<AudioBuffer> {
  const dataInt16 = new Int16Array(data.buffer);
  const frameCount = dataInt16.length / numChannels;
  const buffer = ctx.createBuffer(numChannels, frameCount, sampleRate);

  for (let channel = 0; channel < numChannels; channel++) {
    const channelData = buffer.getChannelData(channel);
    for (let i = 0; i < frameCount; i++) {
      // Convert Int16 back to Float32 [-1, 1]
      channelData[i] = dataInt16[i * numChannels + channel] / 32768.0;
    }
  }
  return buffer;
}

/**
 * Converts a Blob to a Base64 string.
 */
export function blobToBase64(blob: globalThis.Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64data = reader.result as string;
      // Remove the Data URL prefix (e.g., "data:image/jpeg;base64,")
      const base64Content = base64data.split(',')[1];
      resolve(base64Content);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}
